import { useState, useRef } from "react";
import { useLocation } from "react-router-dom";
import { usePipeline } from "./hooks/usePipeline";
import { Sidebar } from "./components/Sidebar";
import { LandingView } from "./components/LandingView";
import { CodeView } from "./components/CodeView";
import { PipelineView } from "./components/PipelineView";
import { GenerationResult } from "./type/types";

export default function Landing() {
  const {
    steps,
    result,
    partialResult,
    partialResultRef,
    chatMessage,
    running,
    paused,
    stopped,
    error,
    stepTokens,
    prompt,
    clarificationQuestion,
    history,
    run,
    answerQuestion,
    stop,
    resume,
  } = usePipeline();

  const location = useLocation();
  const mode: "banking" | "insurance" =
    (location.state as any)?.mode === "insurance" ||
      location.pathname === "/insurvibe-code-builder"
      ? "insurance"
      : "banking";

  const hasEverStartedRef = useRef(false);
  const hasResultRef = useRef(false);

  if (running || stopped || history.length > 0 || steps.some((s) => s.status !== "idle")) {
    hasEverStartedRef.current = true;
  }

  if (result || history.some(h => h.result)) {
    hasResultRef.current = true;
  }

  const hasStarted = hasEverStartedRef.current;

  const [codeViewResult, setCodeViewResult] = useState<GenerationResult | null>(
    null,
  );
  const [codeViewTab, setCodeViewTab] = useState<
    "backend" | "frontend" | "database" | undefined
  >(undefined);
  const [selectedBank, setSelectedBank] = useState<string | null>(null);

  const handleViewCode = (
    passed: GenerationResult,
    tab?: "backend" | "frontend" | "database",
  ) => {
    const latest = partialResultRef.current;
    const effective: GenerationResult = {
      backend:
        passed.backend.length > 0
          ? passed.backend
          : (result?.backend?.length ?? 0) > 0
            ? result!.backend
            : (latest.backend ?? []),
      frontend:
        passed.frontend.length > 0
          ? passed.frontend
          : (result?.frontend?.length ?? 0) > 0
            ? result!.frontend
            : (latest.frontend ?? []),
      database:
        passed.database.length > 0
          ? passed.database
          : (result?.database?.length ?? 0) > 0
            ? result!.database
            : (latest.database ?? []),
    };
    setCodeViewResult(effective);
    setCodeViewTab(tab);
  };
  return (
    <div
      className="h-screen flex overflow-hidden"
      style={{
        background: "#F8F9FC",
        fontFamily: "'DM Sans','Helvetica Neue',system-ui,sans-serif",
      }}
    >
      <Sidebar
        running={running}
        result={result}
        codeViewResult={codeViewResult}
        selectedBank={selectedBank}
        onSelectBank={setSelectedBank}
        mode={mode}
      />

      <main className="flex-1 flex flex-col overflow-hidden">
        {codeViewResult ? (
          <CodeView
            result={codeViewResult}
            defaultTab={codeViewTab}
            onBack={() => {
              setCodeViewResult(null);
              setCodeViewTab(undefined);
            }}
          />
        ) : !hasStarted ? (
          <LandingView
            running={running}
            onRun={run}
            selectedBank={selectedBank}
            mode={mode}
          />
        ) : (
          <PipelineView
            steps={steps}
            stepTokens={stepTokens}
            result={result}
            partialResult={partialResult}
            running={running}
            paused={paused}
            stopped={stopped}
            error={error}
            chatMessage={chatMessage}
            prompt={prompt}
            history={history}
            clarificationQuestion={clarificationQuestion}
            onRun={run}
            onAnswer={answerQuestion}
            onViewCode={handleViewCode}
            onStop={stop}
            onResume={resume}
          />
        )}
      </main>
    </div>
  );
}
